import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Rocket, GitBranch, Radio, Server, Bot, Layers,
  Network, Globe, ShieldCheck, Key, Headphones, Monitor,
  TrendingDown, BarChart3, ArrowRight, ChevronRight,
  Zap, CheckCircle2, AlertTriangle, Clock, Activity,
  Package, Shield, Database, Lock, ShieldAlert,
} from 'lucide-react'
import { useRBACStore } from '@/stores/rbacStore'
import styles from './DashboardPage.module.css'

/* ─── Types ─────────────────────────────────────────────────────────────── */
type Category = 'all' | 'deployments' | 'operations' | 'infrastructure' | 'observability' | 'financial' | 'support'

interface Tile {
  id: string
  title: string
  description: string
  icon: React.ElementType
  route: string
  category: Exclude<Category, 'all'>
  status: 'active' | 'new' | 'beta'
  stats: { label: string; value: string; positive?: boolean }[]
  quickAction: string
  accentVar: string
  shadowVar: string
  bgVar: string
}

/* ─── Tile Definitions ───────────────────────────────────────────────────── */
const TILES: Tile[] = [
  {
    id: 'new-env',
    title: 'New Environment Deployment',
    description: 'Provision Azure AKS clusters and deploy Aptean ERP products end-to-end with Terraform + Helm.',
    icon: Rocket,
    route: '/deployments/new',
    category: 'deployments',
    status: 'active',
    stats: [
      { label: 'Total', value: '142' },
      { label: 'This month', value: '18' },
      { label: 'Avg time', value: '22m' },
    ],
    quickAction: 'Start Deployment',
    accentVar: '--dc-deploy',
    shadowVar: '--dc-shadow-deploy',
    bgVar: '--dc-deploy-bg',
  },
  {
    id: 'release-mgmt',
    title: 'Release Management',
    description: 'Orchestrate versioned releases across environments with approval gates and rollback support.',
    icon: GitBranch,
    route: '/release-management',
    category: 'operations',
    status: 'active',
    stats: [
      { label: 'Pending', value: '3' },
      { label: 'Released', value: '56' },
      { label: 'Success rate', value: '98%', positive: true },
    ],
    quickAction: 'Create Release',
    accentVar: '--dc-ops',
    shadowVar: '--dc-shadow-ops',
    bgVar: '--dc-ops-bg',
  },
  {
    id: 'status-central',
    title: 'Status Central',
    description: 'Real-time health dashboard for all deployed services, AKS nodes, and platform components.',
    icon: Radio,
    route: '/status-central',
    category: 'observability',
    status: 'active',
    stats: [
      { label: 'Healthy', value: '47', positive: true },
      { label: 'Degraded', value: '2' },
      { label: 'Incidents', value: '0', positive: true },
    ],
    quickAction: 'View Status',
    accentVar: '--dc-observe',
    shadowVar: '--dc-shadow-observe',
    bgVar: '--dc-observe-bg',
  },
  {
    id: 'mcp-deploy',
    title: 'MCP Server Deployment',
    description: 'Deploy and manage Model Context Protocol servers for AI integrations across environments.',
    icon: Server,
    route: '/mcp-deployment',
    category: 'deployments',
    status: 'new',
    stats: [
      { label: 'Active', value: '5' },
      { label: 'Environments', value: '3' },
      { label: 'Uptime', value: '99.9%', positive: true },
    ],
    quickAction: 'Deploy MCP',
    accentVar: '--dc-deploy',
    shadowVar: '--dc-shadow-deploy',
    bgVar: '--dc-deploy-bg',
  },
  {
    id: 'agent-deploy',
    title: 'Agent Deployment',
    description: 'Deploy AI agents and automation workers with lifecycle management and scaling controls.',
    icon: Bot,
    route: '/agent-deployment',
    category: 'deployments',
    status: 'new',
    stats: [
      { label: 'Agents', value: '12' },
      { label: 'Running', value: '9' },
      { label: 'Tasks/hr', value: '2.4k' },
    ],
    quickAction: 'Deploy Agent',
    accentVar: '--dc-deploy',
    shadowVar: '--dc-shadow-deploy',
    bgVar: '--dc-deploy-bg',
  },
  {
    id: 'app-in-aks',
    title: 'App in Existing AKS',
    description: 'Deploy new applications to provisioned AKS clusters via Helm without full reprovisioning.',
    icon: Layers,
    route: '/app-in-aks',
    category: 'deployments',
    status: 'active',
    stats: [
      { label: 'Clusters', value: '8' },
      { label: 'Apps', value: '34' },
      { label: 'Namespaces', value: '67' },
    ],
    quickAction: 'Deploy App',
    accentVar: '--dc-deploy',
    shadowVar: '--dc-shadow-deploy',
    bgVar: '--dc-deploy-bg',
  },
  {
    id: 'haproxy',
    title: 'HAProxy Configuration',
    description: 'Manage load balancer backends, health checks, SSL termination and traffic routing rules.',
    icon: Network,
    route: '/haproxy',
    category: 'infrastructure',
    status: 'active',
    stats: [
      { label: 'Backends', value: '24' },
      { label: 'Frontends', value: '8' },
      { label: 'Req/s', value: '12.4k' },
    ],
    quickAction: 'Configure',
    accentVar: '--dc-infra',
    shadowVar: '--dc-shadow-infra',
    bgVar: '--dc-infra-bg',
  },
  {
    id: 'cloudflare',
    title: 'Cloudflare Configuration',
    description: 'Manage DNS records, WAF rules, SSL/TLS modes, page rules and Cloudflare Tunnel connections.',
    icon: Globe,
    route: '/cloudflare',
    category: 'infrastructure',
    status: 'active',
    stats: [
      { label: 'Domains', value: '31' },
      { label: 'DNS Records', value: '248' },
      { label: 'Zones', value: '4' },
    ],
    quickAction: 'Manage DNS',
    accentVar: '--dc-infra',
    shadowVar: '--dc-shadow-infra',
    bgVar: '--dc-infra-bg',
  },
  {
    id: 'patch-mgmt',
    title: 'Patch Management',
    description: 'Automate AKS node pool updates, OS patching, and Helm chart upgrades with maintenance windows.',
    icon: ShieldCheck,
    route: '/patch-management',
    category: 'operations',
    status: 'active',
    stats: [
      { label: 'Pending', value: '7' },
      { label: 'Scheduled', value: '3' },
      { label: 'Applied', value: '89' },
    ],
    quickAction: 'View Patches',
    accentVar: '--dc-ops',
    shadowVar: '--dc-shadow-ops',
    bgVar: '--dc-ops-bg',
  },
  {
    id: 'certificates',
    title: 'Certificate Management',
    description: 'Track SSL/TLS certificate lifecycles, automate renewals via ACME/Let\'s Encrypt, audit expiry.',
    icon: Key,
    route: '/certificates',
    category: 'infrastructure',
    status: 'active',
    stats: [
      { label: 'Active', value: '44' },
      { label: 'Expiring soon', value: '2' },
      { label: 'Auto-renew', value: '41' },
    ],
    quickAction: 'View Certs',
    accentVar: '--dc-infra',
    shadowVar: '--dc-shadow-infra',
    bgVar: '--dc-infra-bg',
  },
  {
    id: 'service-desk',
    title: 'Service Desk',
    description: 'Integrated change management, deployment approvals, incident tracking and SLA management.',
    icon: Headphones,
    route: '/service-desk',
    category: 'support',
    status: 'active',
    stats: [
      { label: 'Open', value: '14' },
      { label: 'Pending', value: '5' },
      { label: 'SLA met', value: '96%', positive: true },
    ],
    quickAction: 'Open Ticket',
    accentVar: '--dc-support',
    shadowVar: '--dc-shadow-support',
    bgVar: '--dc-support-bg',
  },
  {
    id: 'monitoring',
    title: 'Live Monitoring',
    description: 'Real-time Prometheus metrics, Grafana dashboards, alerting, and performance analytics.',
    icon: Monitor,
    route: '/monitoring',
    category: 'observability',
    status: 'active',
    stats: [
      { label: 'Alerts', value: '2' },
      { label: 'Metrics', value: '1.2k' },
      { label: 'Avg latency', value: '48ms' },
    ],
    quickAction: 'Open Dashboard',
    accentVar: '--dc-observe',
    shadowVar: '--dc-shadow-observe',
    bgVar: '--dc-observe-bg',
  },
  {
    id: 'cost-optimize',
    title: 'Cost Optimization',
    description: 'AI-driven right-sizing recommendations, idle resource detection, and FinOps best-practice playbooks.',
    icon: TrendingDown,
    route: '/cost-optimization',
    category: 'financial',
    status: 'beta',
    stats: [
      { label: 'Potential savings', value: '$4.2k' },
      { label: 'Recommendations', value: '18' },
      { label: 'Applied', value: '7' },
    ],
    quickAction: 'View Savings',
    accentVar: '--dc-finance',
    shadowVar: '--dc-shadow-finance',
    bgVar: '--dc-finance-bg',
  },
  {
    id: 'cost-reports',
    title: 'Cost Reports',
    description: 'Monthly spend summaries, budget vs. actuals, showback by environment, tenant, and product family.',
    icon: BarChart3,
    route: '/cost-reports',
    category: 'financial',
    status: 'active',
    stats: [
      { label: 'April spend', value: '$31.4k' },
      { label: 'vs Budget', value: '-8%', positive: true },
      { label: 'Environments', value: '7' },
    ],
    quickAction: 'View Reports',
    accentVar: '--dc-finance',
    shadowVar: '--dc-shadow-finance',
    bgVar: '--dc-finance-bg',
  },
  {
    id: 'disaster-recovery',
    title: 'Disaster Recovery',
    description: 'Centralized DR management - product inventory, RPO/RTO tracking, drill scheduling, backup health, and compliance reporting.',
    icon: ShieldAlert,
    route: '/disaster-recovery',
    category: 'operations',
    status: 'new',
    stats: [
      { label: 'Products', value: '0' },
      { label: 'Drills YTD', value: '0' },
      { label: 'DR Readiness', value: 'N/A' },
    ],
    quickAction: 'Open DR Hub',
    accentVar: '--dc-ops',
    shadowVar: '--dc-shadow-ops',
    bgVar: '--dc-ops-bg',
  },
  {
    id: 'cmdb',
    title: 'CMDB',
    description: 'Configuration Management Database — full infrastructure inventory, dependency mapping, and CI lifecycle tracking.',
    icon: Database,
    route: '/cmdb',
    category: 'observability',
    status: 'active',
    stats: [
      { label: 'Total CIs',  value: '248' },
      { label: 'Healthy',    value: '231', positive: true },
      { label: 'Relations',  value: '1.9k' },
    ],
    quickAction: 'View CMDB',
    accentVar: '--dc-observe',
    shadowVar: '--dc-shadow-observe',
    bgVar: '--dc-observe-bg',
  },
]

const CATEGORIES: { id: Category; label: string }[] = [
  { id: 'all',            label: 'All Modules' },
  { id: 'deployments',    label: 'Deployments' },
  { id: 'operations',     label: 'Operations' },
  { id: 'infrastructure', label: 'Network Config' },
  { id: 'observability',  label: 'Observability' },
  { id: 'financial',      label: 'Financial' },
  { id: 'support',        label: 'Support' },
]

const PLATFORM_STATS = [
  { icon: Rocket,        label: 'Deployments',    value: '142',   delta: '+18 this month', positive: true  },
  { icon: CheckCircle2,  label: 'Success Rate',   value: '97.2%', delta: '+0.4% vs last',  positive: true  },
  { icon: Activity,      label: 'Active Services',value: '47',    delta: '2 degraded',     positive: false },
  { icon: AlertTriangle, label: 'Open Incidents', value: '2',     delta: '0 critical',     positive: true  },
  { icon: Clock,         label: 'Avg Deploy Time',value: '22m',   delta: '-3m vs last wk', positive: true  },
  { icon: Package,       label: 'Products',       value: '7',     delta: 'Across 7 envs',  positive: true  },
]

/* ─── Tile Card ─────────────────────────────────────────────────────────── */
function TileCard({ tile }: { tile: Tile }) {
  const navigate   = useNavigate()
  const { canView, canAct } = useRBACStore()
  const Icon = tile.icon

  const hasView   = canView(tile.id)
  const hasAction = canAct(tile.id)

  const badgeLabel = tile.status === 'active' ? 'Active' : tile.status === 'new' ? 'New' : 'Beta'
  const badgeCls   = tile.status === 'active' ? styles.badgeActive
    : tile.status === 'new' ? styles.badgeNew : styles.badgeBeta

  // Reader/Viewer without view access: render locked tile
  if (!hasView) {
    return (
      <div
        className={`${styles.tile} ${styles.tileLocked}`}
        style={{
          '--tile-accent': `var(${tile.accentVar})`,
          '--tile-shadow': `var(${tile.shadowVar})`,
          '--tile-bg':     `var(${tile.bgVar})`,
        } as React.CSSProperties}
        title="You don't have permission to access this module"
      >
        <div className={styles.tileAccent} />
        <div className={styles.tileTop}>
          <div className={`${styles.tileIconWrap} ${styles.tileIconLocked}`}>
            <Icon size={22} />
          </div>
          <Lock size={14} className={styles.lockIcon} />
        </div>
        <div className={styles.tileBody}>
          <h3 className={styles.tileTitle}>{tile.title}</h3>
          <p className={styles.tileDesc}>{tile.description}</p>
        </div>
        <div className={styles.tileFooter}>
          <div className={styles.tileAccessDenied}>
            <Lock size={12} /> Access restricted
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={styles.tile}
      style={{
        '--tile-accent':  `var(${tile.accentVar})`,
        '--tile-shadow':  `var(${tile.shadowVar})`,
        '--tile-bg':      `var(${tile.bgVar})`,
      } as React.CSSProperties}
      onClick={() => navigate(tile.route)}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && navigate(tile.route)}
    >
      <div className={styles.tileAccent} />

      <div className={styles.tileTop}>
        <div className={styles.tileIconWrap}>
          <Icon size={22} />
        </div>
        <span className={`${styles.tileBadge} ${badgeCls}`}>{badgeLabel}</span>
      </div>

      <div className={styles.tileBody}>
        <h3 className={styles.tileTitle}>{tile.title}</h3>
        <p className={styles.tileDesc}>{tile.description}</p>
      </div>

      <div className={styles.tileStats}>
        {tile.stats.map(s => (
          <div key={s.label} className={styles.tileStat}>
            <span className={`${styles.tileStatValue} ${s.positive ? styles.tileStatPos : ''}`}>
              {s.value}
            </span>
            <span className={styles.tileStatLabel}>{s.label}</span>
          </div>
        ))}
      </div>

      <div className={styles.tileFooter}>
        {hasAction ? (
          <button
            className={styles.tileAction}
            onClick={e => { e.stopPropagation(); navigate(tile.route) }}
          >
            {tile.quickAction}
            <ChevronRight size={14} />
          </button>
        ) : (
          <button
            className={`${styles.tileAction} ${styles.tileActionReadOnly}`}
            onClick={e => { e.stopPropagation(); navigate(tile.route) }}
          >
            <Shield size={12} /> View Only
          </button>
        )}
      </div>
    </div>
  )
}

/* ─── Dashboard Page ─────────────────────────────────────────────────────── */
export function DashboardPage() {
  const navigate = useNavigate()
  const { currentRole, setRole } = useRBACStore()
  const [activeCategory, setActiveCategory] = useState<Category>('all')
  const [search, setSearch] = useState('')

  const filtered = TILES.filter(t => {
    const matchCat    = activeCategory === 'all' || t.category === activeCategory
    const matchSearch = !search
      || t.title.toLowerCase().includes(search.toLowerCase())
      || t.description.toLowerCase().includes(search.toLowerCase())
    return matchCat && matchSearch
    // NOTE: we always show tiles (locked state for no-access), so admins can see what exists
  })

  return (
    <div className={styles.page}>

      {/* ── Hero Banner ── */}
      <div className={styles.hero}>
        <div className={styles.heroContent}>
          <div className={styles.heroText}>
            <div className={styles.heroBreadcrumb}>
              <Shield size={13} />
              <span>Aptean Platform</span>
              <ChevronRight size={12} />
              <span>Platform Central</span>
            </div>
            <h1 className={styles.heroTitle}>Platform Central</h1>
            <p className={styles.heroSub}>
              Enterprise operations hub — deploy, manage, and monitor Aptean ERP infrastructure
              across Azure environments from a single pane of glass.
            </p>
            <div className={styles.heroActions}>
              <button
                className={styles.heroBtnPrimary}
                onClick={() => navigate('/deployments/new')}
              >
                <Rocket size={15} />
                New Deployment
              </button>
              <button
                className={styles.heroBtnSecondary}
                onClick={() => navigate('/status-central')}
              >
                <Radio size={15} />
                System Status
              </button>
            </div>

            {/* Role indicator */}
            <div className={styles.roleBar}>
              <Shield size={12} />
              <span>Viewing as:</span>
              <div className={styles.roleSelector}>
                {(['reader', 'viewer', 'contributor', 'admin'] as const).map(r => (
                  <button
                    key={r}
                    className={`${styles.roleBtn} ${currentRole === r ? styles.roleBtnActive : ''}`}
                    onClick={() => setRole(r)}
                  >
                    {r.charAt(0).toUpperCase() + r.slice(1)}
                  </button>
                ))}
              </div>
              <span className={styles.roleHint}>
                {currentRole === 'reader' ? '— read-only, status/CMDB only' :
                 currentRole === 'viewer' ? '— read-only, all tiles' :
                 currentRole === 'contributor' ? '— can deploy and configure' :
                 '— full access'}
              </span>
            </div>
          </div>
          <div className={styles.heroBadge}>
            <Zap size={28} />
            <div className={styles.heroBadgeNum}>15</div>
            <div className={styles.heroBadgeSub}>Modules</div>
          </div>
        </div>

        {/* Platform stats strip */}
        <div className={styles.heroStats}>
          {PLATFORM_STATS.map(s => {
            const Icon = s.icon
            return (
              <div key={s.label} className={styles.heroStat}>
                <div className={styles.heroStatIcon}><Icon size={14} /></div>
                <div className={styles.heroStatBody}>
                  <div className={styles.heroStatValue}>{s.value}</div>
                  <div className={styles.heroStatLabel}>{s.label}</div>
                  <div className={`${styles.heroStatDelta} ${s.positive ? styles.deltaPos : styles.deltaNeg}`}>
                    {s.delta}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* ── Filter + Search ── */}
      <div className={styles.controls}>
        <div className={styles.categoryFilters}>
          {CATEGORIES.map(c => (
            <button
              key={c.id}
              className={`${styles.catBtn} ${activeCategory === c.id ? styles.catBtnActive : ''}`}
              onClick={() => setActiveCategory(c.id)}
            >
              {c.label}
              {c.id !== 'all' && (
                <span className={styles.catCount}>
                  {TILES.filter(t => t.category === c.id).length}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className={styles.searchBox}>
          <Activity size={13} className={styles.searchBoxIcon} />
          <input
            className={styles.searchInput}
            placeholder="Search modules..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className={styles.searchClear} onClick={() => setSearch('')}>✕</button>
          )}
        </div>
      </div>

      {/* ── Tile Grid ── */}
      {filtered.length === 0 ? (
        <div className={styles.empty}>
          <Activity size={36} className={styles.emptyIcon} />
          <div className={styles.emptyTitle}>No modules match your filters</div>
          <button
            className={styles.emptyReset}
            onClick={() => { setSearch(''); setActiveCategory('all') }}
          >
            Reset filters
          </button>
        </div>
      ) : (
        <div className={styles.grid}>
          {filtered.map(tile => <TileCard key={tile.id} tile={tile} />)}
        </div>
      )}

      {/* ── Quick Links ── */}
      <div className={styles.quickLinks}>
        <div className={styles.quickLinksTitle}>Quick Access</div>
        <div className={styles.quickLinksRow}>
          {[
            { label: 'Browse Products',  route: '/products',        icon: Package    },
            { label: 'Admin Console',    route: '/admin',           icon: Shield     },
            { label: 'All Deployments',  route: '/deployments',     icon: Rocket     },
            { label: 'Cost Reports',     route: '/cost-reports',    icon: BarChart3  },
            { label: 'Service Desk',     route: '/service-desk',    icon: Headphones },
          ].map(({ label, route, icon: Icon }) => (
            <button key={route} className={styles.quickLink} onClick={() => navigate(route)}>
              <Icon size={14} />
              {label}
              <ArrowRight size={12} />
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
